import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
  useMemo,
  useRef,
} from 'react';
import { observer } from 'mobx-react';
import {
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import styled from 'styled-components';
import Dialog from 'components/UI/Dialog';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import Space from 'components/UI/Space';
import { DialogOnCloseReason, ActionMode } from 'models/shared';
import { useStores } from 'stores/hooks/hooks';
import { Campaign, CampaignStatus } from 'models/restTypes';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Chips, ChipsChangeEvent } from 'primereact/chips';
import api from 'Api/ApiMethods';
import Loading from 'components/UI/Loading/Loading';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { usePapaParse } from 'react-papaparse';
import { set } from 'date-fns';

const StyledSelect = styled(Select)`
  background-color: light-dark(var(--surface-ground), var(--surface-200));
  color: var(--text-color);
  border: 1px solid var(--surface-border);

  .Mui-disabled {
    color: var(--text-color-secondary);
    -webkit-text-fill-color: var(--text-color-secondary);
    opacity: 0.42;
  }

  ::placeholder {
    color: var(--text-color-secondary);
  }
`;

const StyledMultiSelect = styled(MultiSelect)``;

const StyledChips = styled(Chips)`
  .p-chips-multiple-container {
    width: 100%;
  }
`;

const StyledFileUpload = styled(FileUpload)`
  .p-button {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
  }
`;

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
  text-align: left;
`;

interface CampaignEdit extends Omit<Campaign, 'uuid' | 'agentId'> {
  uuid?: string;
  agentId: number | null;
}

const EMPTY_CAMPAIGN: CampaignEdit = {
  externalUuid: null,
  name: '',
  b2bAccountEmail: null,
  description: '',
  status: CampaignStatus.APPROVED,
  messageText: '',
  imageUri: '',
  agentId: 7,
  areas: [],
  cropTypeIds: [],
  countryCodes: [],
  notificationTypeIds: [],
};

const DEFAULT_NOTIFICATION_TYPE_ID = 1; // Set this to the appropriate default ID based on your application's needs

const campaignStatusOptions: CampaignStatus[] = [
  CampaignStatus.APPROVED,
  CampaignStatus.COMPLETED,
  CampaignStatus.DELETED,
  CampaignStatus.PENDING,
  CampaignStatus.REJECTED,
  CampaignStatus.STOPPED,
];

interface CampaignManagementDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
  campaign?: Campaign | null;
}

const CampaignManagementDialog: FC<CampaignManagementDialogProps> = observer(
  ({ visible = false, onHide, title, mode, campaign }) => {
    const { t } = useTranslation('campaigns');
    const [isLoading, setIsLoading] = useState(true);
    const { campaignsStore } = useStores();
    const [cropTypes, setCropTypes] = useState<
      { value: number; label: string }[]
    >([]);

    const [notificationTypeIds, setNotificationTypeIds] = useState<
      { value: number; label: string }[]
    >([]);

    const [campaignEdit, setCampaignEdit] = useState<CampaignEdit>(
      campaign || EMPTY_CAMPAIGN,
    );

    const [fieldHasError, setFieldHasError] = useState<{
      [key: string]: boolean;
    }>({});

    useEffect(() => {
      if (campaign && mode === ActionMode.EDIT) {
        setCampaignEdit(campaign);
      } else {
        setCampaignEdit(EMPTY_CAMPAIGN);
      }
    }, [campaign, mode]);

    const hasError = useMemo(
      () => Object.values(fieldHasError).some((error) => error),
      [fieldHasError],
    );

    const isValidForm = useMemo(() => {
      if (
        campaignEdit.name?.trim() &&
        campaignEdit.messageText?.trim() &&
        campaignEdit.agentId != null &&
        !hasError
      ) {
        return true;
      }

      return false;
    }, [campaignEdit, hasError]);

    const closeDialog = useCallback(() => {
      setCampaignEdit(EMPTY_CAMPAIGN);
      setFieldHasError({});
      onHide();
    }, [onHide]);

    const handleCloseDialog = useCallback(
      (_event: unknown, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        closeDialog();
      },
      [closeDialog],
    );

    const handleCloseButton = useCallback(() => {
      closeDialog();
    }, [closeDialog]);

    const handleChange = useCallback(
      (
        event: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const { value } = event.target;
        setCampaignEdit((prev) => ({
          ...prev,
          [event.target.name]: value,
        }));
      },
      [],
    );

    const handleNumberChange = useCallback(
      (
        event: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const { value } = event.target;
        setCampaignEdit((prev) => ({
          ...prev,
          [event.target.name]: value === '' ? 0 : Number(value),
        }));
      },
      [],
    );

    const handleStatusChange = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        const { value } = event.target;
        if (value) {
          setCampaignEdit((prev) => ({
            ...prev,
            status: value as string,
          }));
        }
      },
      [],
    );

    const handleAreasChange = useCallback((event: ChipsChangeEvent) => {
      const valueArray = event.value as string[];
      setCampaignEdit((prev) => ({
        ...prev,
        areas: valueArray || [],
      }));
    }, []);

    const handleCountryCodesChange = useCallback((event: ChipsChangeEvent) => {
      const valueArray = event.value as string[];
      setCampaignEdit((prev) => ({
        ...prev,
        countryCodes: valueArray || [],
      }));
    }, []);

    const handleCropTypeIdsChange = useCallback(
      (event: MultiSelectChangeEvent) => {
        const valueArray = event.value as number[];
        setCampaignEdit((prev) => ({
          ...prev,
          cropTypeIds: valueArray,
        }));
      },
      [],
    );

    const handleNotificationTypeIdsChange = useCallback(
      (event: MultiSelectChangeEvent) => {
        const valueArray = event.value as number[];
        setCampaignEdit((prev) => ({
          ...prev,
          notificationTypeIds: valueArray,
        }));
      },
      [],
    );

    console.log('campaignEdit', campaignEdit)

    const handleSubmitClick = useCallback(async () => {
      if (isValidForm) {
        if (mode === ActionMode.CREATE) {
          await campaignsStore.createCampaign(campaignEdit as Campaign);
        } else {
          await campaignsStore.updateCampaign(campaignEdit as Campaign);
        }

        onHide();
      }
    }, [campaignEdit, isValidForm, onHide, mode, campaignsStore]);

    const getFieldData = async () => {
      try {
        setIsLoading(true);
        const res = await api.getCropTypes();
        const notificationTypeRes = await api.getNotificationTypes();
        const formattedNotificationTypes = (notificationTypeRes || []).map(
          (notificationType: { uid: number; name: string }) => ({
            value: notificationType.uid,
            label: `(${notificationType.uid}) - ${notificationType.name}`,
          }),
        );

        const formattedCropTypes = (res || []).map(
          (cropType: { id: number; name: string }) => ({
            value: cropType.id,
            label: cropType.name,
          }),
        );

        if (res) {
          setCropTypes(formattedCropTypes);
        }

        if (notificationTypeRes) {
          setNotificationTypeIds(formattedNotificationTypes);
        }

        // SET DEFAULT NOTIFICATION TYPE ID IF CREATING A NEW CAMPAIGN
        if (mode === ActionMode.CREATE) {
          const initialDefaultNotificationTypeId =
            formattedNotificationTypes.find(
              (notif) => notif.value === DEFAULT_NOTIFICATION_TYPE_ID,
            )?.value ||
            formattedNotificationTypes[0]?.value ||
            null;

          if (initialDefaultNotificationTypeId) {
            setCampaignEdit((prevCampaign) => ({
              ...prevCampaign,
              notificationTypeIds: [initialDefaultNotificationTypeId],
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching crop types or notification types:', error);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      getFieldData();
    }, []);

    const { readString } = usePapaParse();

    const fileUploadRef = useRef<FileUpload | null>(null);

    const handleImportCSV = useCallback(
      (event: FileUploadSelectEvent) => {
        const file = event.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          if (!text) return;

          readString<Record<string, string>>(text, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              if (results.data && results.data.length > 0) {
                const row = results.data[0];

                // Parse crop types from CSV (convert names to IDs)
                let cropTypeIds: number[] = [];
                if (row.crop_types) {
                  try {
                    const cropNames = JSON.parse(row.crop_types);
                    cropTypeIds = cropTypes
                      .filter((ct) => cropNames.includes(ct.label))
                      .map((ct) => ct.value);
                  } catch (err) {
                    // If parsing fails, try comma-separated
                    const cropNames = row.crop_types
                      .split(',')
                      .map((s: string) => s.trim());

                    cropTypeIds = cropTypes
                      .filter((ct) => cropNames.includes(ct.label))
                      .map((ct) => ct.value);
                  }
                }

                // Parse arrays from CSV
                const parseArray = (value: string): string[] => {
                  if (!value) return [];
                  try {
                    return JSON.parse(value);
                  } catch (parseErr) {
                    return value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                  }
                };

                setCampaignEdit((prev) => ({
                  ...prev,
                  externalUuid: row.ID || prev.externalUuid,
                  name: row.title || prev.name,
                  messageText: row.message || prev.messageText,
                  status: row.status?.toUpperCase() || prev.status,
                  b2bAccountEmail: row.created_by_email || prev.b2bAccountEmail,
                  countryCodes: parseArray(row.country),
                  areas: parseArray(row.regions),
                  cropTypeIds,
                }));
              }

              // Clear the file input so the same file can be selected again
              if (fileUploadRef.current) {
                fileUploadRef.current.clear();
              }
            },
          });
        };

        reader.readAsText(file);
      },
      [cropTypes, readString],
    );

    if (isLoading) {
      return (
        <Dialog
          open={visible}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
          dialogTitle={title}
          titleWeight="bold"
          disableEscapeKeyDown
        >
          <DialogContent>
            <Space height={2} />
            <Loading />
            <Space height={2} />
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <Space height={1} />
          <Grid direction="column" gap="10px">

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('import_campaign_csv')}:</NoWrappingText>
              <StyledFileUpload
                ref={fileUploadRef}
                mode="basic"
                accept=".csv"
                maxFileSize={1000000}
                onSelect={handleImportCSV}
                customUpload
                chooseLabel={t('select_csv_file') || 'Choose CSV File'}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_name')}:</NoWrappingText>
              <Input
                required
                name="name"
                label={t('create_dialog_name')}
                id="name"
                value={campaignEdit.name}
                variant="outlined"
                type="text"
                fullWidth
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_external_uuid')}:
              </NoWrappingText>
              <Input
                name="externalUuid"
                label={t('create_dialog_external_uuid')}
                id="externalUuid"
                value={campaignEdit.externalUuid || ''}
                variant="outlined"
                type="text"
                fullWidth
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_description')}</NoWrappingText>
              <Input
                name="description"
                label={t('create_dialog_description')}
                id="description"
                value={campaignEdit.description}
                variant="outlined"
                type="text"
                fullWidth
                multiline
                rows={2}
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_message_text')}:
              </NoWrappingText>
              <Input
                required
                name="messageText"
                label={t('create_dialog_message_text')}
                id="messageText"
                value={campaignEdit.messageText}
                variant="outlined"
                type="text"
                fullWidth
                multiline
                rows={3}
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_image_uri')}:</NoWrappingText>
              <Input
                required
                name="imageUri"
                label={t('create_dialog_image_uri')}
                id="imageUri"
                value={campaignEdit.imageUri}
                variant="outlined"
                type="text"
                fullWidth
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_b2b_account_email')}:
              </NoWrappingText>
              <Input
                name="b2bAccountEmail"
                label={t('create_dialog_b2b_account_email')}
                id="b2bAccountEmail"
                value={campaignEdit.b2bAccountEmail || ''}
                variant="outlined"
                type="email"
                fullWidth
                onChange={handleChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_status')}:</NoWrappingText>
              <StyledSelect
                value={campaignEdit.status}
                name="status"
                onChange={handleStatusChange}
                fullWidth
              >
                {campaignStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_agent_id')}:</NoWrappingText>
              <Input
                required
                name="agentId"
                label={t('create_dialog_agent_id')}
                id="agentId"
                value={campaignEdit.agentId}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_country_codes')}:
              </NoWrappingText>
              <StyledChips
                id="countryCodes"
                value={campaignEdit.countryCodes}
                onChange={handleCountryCodesChange}
                placeholder={t('create_dialog_country_codes_placeholder')}
                style={{ width: '100%' }}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('create_dialog_areas')}:</NoWrappingText>
              <StyledChips
                id="areas"
                value={campaignEdit.areas}
                onChange={handleAreasChange}
                placeholder={t('create_dialog_areas_placeholder')}
                style={{ width: '100%' }}
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_crop_type_ids')}:
              </NoWrappingText>
              <StyledMultiSelect
                id="cropTypeIds"
                options={cropTypes}
                value={campaignEdit.cropTypeIds}
                onChange={handleCropTypeIdsChange}
                showSelectAll={false}
                placeholder="Enter crop type IDs"
                filter
                filterPlaceholder="Search crop types"
              />
            </Grid>

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>
                {t('create_dialog_notification_type_ids')}:
              </NoWrappingText>
              <StyledMultiSelect
                id="notificationTypeIds"
                options={notificationTypeIds}
                value={campaignEdit.notificationTypeIds}
                onChange={handleNotificationTypeIdsChange}
                showSelectAll={false}
                placeholder="Enter notification type IDs"
                filter
                filterPlaceholder="Search notification types"
              />
              {/* <StyledChips
                id="notificationTypeIds"
                value={campaignEdit.notificationTypeIds.map(String)}
                onChange={handleNotificationTypeIdsChange}
                placeholder={t(
                  'create_dialog_notification_type_ids_placeholder',
                )}
                style={{ width: '100%' }}
              /> */}
            </Grid>
          </Grid>
          <Space height={1} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleSubmitClick} disabled={!isValidForm}>
            <Text textcolor={theme.color.white} size="sm">
              {t('ok')}
            </Text>
          </Button>
          <Button onClick={handleCloseButton}>
            <Text textcolor={theme.color.white} size="sm">
              {t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CampaignManagementDialog;
